import {
  Add,
  Close,
  Delete,
  Person,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname } from "../../../utils";
import { ROLE_OWNER } from "../../../constants";

interface CompanyUser {
  uid: string;
  email: string;
  role: string;
  displayName?: string;
}

interface CompanyUsersDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  whitelabelId: string;
  companyId: string;
  companyName: string;
  users: CompanyUser[];
  seats: number | null;
  onUsersChanged: () => void;
}

export default function CompanyUsersDialog({
  open,
  onClose,
  getToken,
  whitelabelId,
  companyId,
  companyName,
  users: initialUsers,
  seats,
  onUsersChanged,
}: CompanyUsersDialogProps) {
  const [users, setUsers] = useState<CompanyUser[]>(initialUsers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Add user form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addFirstName, setAddFirstName] = useState("");
  const [addLastName, setAddLastName] = useState("");
  const [addRole, setAddRole] = useState("member");
  const [addLoading, setAddLoading] = useState(false);

  // Delete state
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // Search state
  const [search, setSearch] = useState("");

  const maxSeats = seats || 1;
  const canAddMore = users.length < maxSeats;

  useEffect(() => {
    if (open) {
      setUsers(initialUsers);
      setError("");
      setSuccess("");
      setShowAddForm(false);
      setAddEmail("");
      setAddFirstName("");
      setAddLastName("");
      setAddRole("member");
      setSearch("");
    }
  }, [open, initialUsers]);

  const handleAddUser = async () => {
    if (!addEmail.trim()) {
      setError("Email is required");
      return;
    }

    setAddLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/whitelabel/${whitelabelId}/companies/${companyId}/users`,
        {
          email: addEmail.trim(),
          firstName: addFirstName.trim(),
          lastName: addLastName.trim(),
          role: addRole,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const newUser = response.data.user;
        setUsers((prev) => [...prev, newUser]);
        setSuccess(`Successfully added ${addEmail} to the company`);
        setShowAddForm(false);
        setAddEmail("");
        setAddFirstName("");
        setAddLastName("");
        setAddRole("member");
        onUsersChanged();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add user");
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string, userEmail: string) => {
    setDeletingUserId(userId);
    setError("");
    setSuccess("");

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/whitelabel/${whitelabelId}/companies/${companyId}/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers((prev) => prev.filter((u) => u.uid !== userId));
      setSuccess(`Successfully removed ${userEmail} from the company`);
      onUsersChanged();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to remove user");
    } finally {
      setDeletingUserId(null);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(search.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case ROLE_OWNER:
        return "Owner";
      case "admin":
        return "Admin";
      case "member":
        return "Member";
      default:
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  const getRoleColor = (
    role: string
  ): "primary" | "secondary" | "default" => {
    switch (role) {
      case ROLE_OWNER:
        return "primary";
      case "admin":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6">Manage Users</Typography>
          <Typography variant="body2" color="text.secondary">
            {companyName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Header with stats and add button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {users.length} / {maxSeats} seats used
            </Typography>
            {!canAddMore && (
              <Chip
                label="Seat limit reached"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
          {!showAddForm && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowAddForm(true)}
              disabled={!canAddMore}
              size="small"
            >
              Add User
            </Button>
          )}
        </Box>

        {/* Add User Form */}
        {showAddForm && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              Add New User
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  fullWidth
                  size="small"
                  type="email"
                  required
                  placeholder="user@example.com"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="First Name"
                  value={addFirstName}
                  onChange={(e) => setAddFirstName(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="Last Name"
                  value={addLastName}
                  onChange={(e) => setAddLastName(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  label="Role"
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value)}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value={ROLE_OWNER}>Owner</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <LoadingButton
                    variant="contained"
                    onClick={handleAddUser}
                    loading={addLoading}
                    size="small"
                  >
                    Add
                  </LoadingButton>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowAddForm(false);
                      setAddEmail("");
                      setAddFirstName("");
                      setAddLastName("");
                      setAddRole("member");
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              If the user doesn't exist, they will be created and can log in
              using the email provided.
            </Typography>
          </Box>
        )}

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search users by email or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Users Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Person sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {search
                ? "No users match your search"
                : "No users in this company yet"}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.uid} hover>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        <Box>
                          {user.displayName && (
                            <Typography variant="body2" fontWeight="medium">
                              {user.displayName}
                            </Typography>
                          )}
                          <Typography
                            variant={user.displayName ? "caption" : "body2"}
                            color={
                              user.displayName
                                ? "text.secondary"
                                : "text.primary"
                            }
                          >
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleDisplayName(user.role)}
                        size="small"
                        color={getRoleColor(user.role)}
                        variant={
                          user.role === ROLE_OWNER ? "filled" : "outlined"
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove user from company">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveUser(user.uid, user.email)}
                            disabled={deletingUserId === user.uid}
                          >
                            {deletingUserId === user.uid ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Delete />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
